import { useEffect, useRef, useState } from 'react';
import { DEVELOPMENT_MAP_LOCATIONS } from '../data/developmentMapLocations';
import {
  createDevelopmentMapPinIcon,
  loadLeaflet,
  type LeafletLib,
  type LeafletMap,
  type LeafletMarker,
} from '../utils/loadLeaflet';

type DevelopmentsMapProps = {
  selectedId: string | null;
};

function DevelopmentsMap({ selectedId }: DevelopmentsMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<Record<string, LeafletMarker>>({});
  const leafletRef = useRef<LeafletLib | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current) return;

        leafletRef.current = L;
        const map = L.map(containerRef.current, { scrollWheelZoom: true });
        mapRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        const bounds = L.latLngBounds(
          DEVELOPMENT_MAP_LOCATIONS.map((home) => [home.lat, home.lng] as [number, number])
        );

        const markers: Record<string, LeafletMarker> = {};
        DEVELOPMENT_MAP_LOCATIONS.forEach((home) => {
          const marker = L.marker([home.lat, home.lng], {
            icon: createDevelopmentMapPinIcon(L, false),
          }).addTo(map);

          marker.bindPopup(
            `<div class="developments-map-popup"><strong>${home.name}</strong><br /><span>${home.location}</span></div>`
          );

          markers[home.id] = marker;
        });

        markersRef.current = markers;
        map.fitBounds(bounds, { padding: [48, 48], maxZoom: 11 });
        setMapReady(true);
      })
      .catch(() => {
        if (containerRef.current) {
          containerRef.current.innerHTML =
            '<p class="developments-map-fallback">Map could not be loaded. Please try again later.</p>';
        }
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = {};
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const L = leafletRef.current;
    if (!mapReady || !map || !L || !selectedId) return;

    const location = DEVELOPMENT_MAP_LOCATIONS.find((entry) => entry.id === selectedId);
    if (!location) return;

    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const isActive = id === selectedId;
      marker.setIcon(createDevelopmentMapPinIcon(L, isActive));
      if (isActive) {
        marker.openPopup();
      }
    });

    map.panTo([location.lat, location.lng], { animate: true, duration: 0.85 });
  }, [selectedId, mapReady]);

  return (
    <section
      id="developments-map"
      className="developments-map-section"
      aria-label="Map of Graham Care developments"
    >
      <h2>Our developments on the map</h2>
      <p className="developments-map-intro">
        Click &ldquo;Show on Map&rdquo; on any project to highlight its location.
      </p>
      <div ref={containerRef} className="developments-map__container" role="application" />
    </section>
  );
}

export default DevelopmentsMap;
